import { MutationResult } from "@/api/utils/types"
import {
  IContactForm,
  IContactUpdate,
  IContactUpdateComms,
  IContactRemove,
} from "./ContactMutator.types"
import { buildUpdateContactInput } from "./ContactMutator.helpers"
import { useMutation } from "@/api/utils/useQuery"
import {
  BackofficeRestMutations,
  CreateAccountContactPayload,
  UpdateAccountContactPayload,
} from "@/api/backofficeRest/mutations"
import { useCurrentAccountStore } from "@/stores/currentAccount"
import { AccountContact } from "@/api/backofficeRest/generated/model"

const requireBillingAccountId = (): number => {
  const billingAccountId = useCurrentAccountStore().coreId.data
  if (!billingAccountId) throw new Error("Missing coreId")
  return billingAccountId
}

export interface IContactMutator {
  addForAccount(): MutationResult<IContactForm, AccountContact>
  updateForAccount(): MutationResult<IContactUpdate, void>
  updateCommsForAccount(): MutationResult<IContactUpdateComms, void>
  removeForAccount(): MutationResult<IContactRemove, void>
}

class ContactMutatorClass implements IContactMutator {
  addForAccount() {
    return useMutation({
      ...BackofficeRestMutations.account.createContact(),
      serializerFn: (data: IContactForm): CreateAccountContactPayload => ({
        billingAccountId: requireBillingAccountId(),
        body: {
          contactType: data.contactType ?? "Residential",
          surname: data.surname ?? "",
          primary: data.isPrimary ?? false,
          ...(data.title && { title: data.title }),
          ...(data.forename && { forename: data.forename }),
          ...(data.email && { email: data.email }),
          ...(data.phone1 && { phone1: data.phone1 }),
          ...(data.phone2 && { phone2: data.phone2 }),
          address: {
            address1: data.address?.address1 ?? "",
            address2: data.address?.address2 ?? "",
            address3: data.address?.address3 ?? "",
            postcode: data.address?.postcode ?? "",
          },
        },
      }),
    })
  }

  updateForAccount() {
    return useMutation({
      ...BackofficeRestMutations.account.updateContact(),
      serializerFn: (data: IContactUpdate): UpdateAccountContactPayload => ({
        billingAccountId: requireBillingAccountId(),
        juniferContactId: data.contact.juniferContactId,
        body: buildUpdateContactInput({
          contact: data.contact,
          edits: data.edits,
        }),
      }),
    })
  }

  updateCommsForAccount() {
    return useMutation({
      ...BackofficeRestMutations.account.updateContact(),
      serializerFn: (
        data: IContactUpdateComms,
      ): UpdateAccountContactPayload => ({
        billingAccountId: requireBillingAccountId(),
        juniferContactId: data.contact.juniferContactId,
        body: buildUpdateContactInput({
          contact: data.contact,
          comms: {
            receivePost: data.receivePost,
            receiveEmail: data.receiveEmail,
            receiveSMS: data.receiveSMS,
          },
          extra: { billDelivery: data.billDelivery },
        }),
      }),
    })
  }

  removeForAccount() {
    return useMutation({
      ...BackofficeRestMutations.account.updateContact(),
      serializerFn: (data: IContactRemove): UpdateAccountContactPayload => ({
        billingAccountId: requireBillingAccountId(),
        juniferContactId: data.contact.juniferContactId,
        body: buildUpdateContactInput({
          contact: data.contact,
          extra: { cancelled: true },
        }),
      }),
    })
  }
}

export const ContactMutator = new ContactMutatorClass()
